// src/core/conversation.ts — append-and-update conversation store (grounding §4),
// behaviour-identical to LLABARO.jsx conversationReducer. Foreign partials update the
// single open turn in place; native turns are committed.
import type { ConversationState, LanguagePair, Turn, TurnStatus } from "./models";

export const emptyConversation: ConversationState = {
  turns: [],
  openForeign: null,
  counter: 0,
};

export type ConvAction =
  | { type: "FOREIGN_PARTIAL"; source?: string; target?: string; pair: LanguagePair }
  | { type: "FOREIGN_FINAL" }
  | {
      type: "NATIVE_ADD";
      source: string;
      target: string;
      pair: LanguagePair;
      status?: TurnStatus;
    }
  | { type: "NATIVE_UPDATE"; id: string; patch: Partial<Turn> }
  | { type: "MARK_SPOKEN"; id: string }
  | { type: "CLEAR" };

function applyPartial(
  state: ConversationState,
  a: { source?: string; target?: string }
): ConversationState {
  const turns = state.turns.slice();
  const i = state.openForeign!;
  turns[i] = {
    ...turns[i],
    sourceText: a.source !== undefined ? a.source : turns[i].sourceText,
    targetText: a.target !== undefined ? a.target : turns[i].targetText,
  };
  return { ...state, turns };
}

export function conversationReducer(
  state: ConversationState,
  action: ConvAction
): ConversationState {
  switch (action.type) {
    case "FOREIGN_PARTIAL": {
      if (state.openForeign === null) {
        const turns = state.turns.slice();
        const idx = turns.length;
        turns.push({
          id: "t" + (state.counter + 1),
          side: "foreign",
          sourceText: "",
          targetText: "",
          sourceLang: action.pair.foreign,
          targetLang: action.pair.native,
          status: "partial",
          spoken: false,
          ts: Date.now(),
        });
        return applyPartial(
          { ...state, turns, openForeign: idx, counter: state.counter + 1 },
          action
        );
      }
      return applyPartial(state, action);
    }
    case "FOREIGN_FINAL": {
      if (state.openForeign === null) return state;
      const turns = state.turns.slice();
      turns[state.openForeign] = { ...turns[state.openForeign], status: "final" };
      return { ...state, turns, openForeign: null };
    }
    case "NATIVE_ADD": {
      const turns = state.turns.slice();
      turns.push({
        id: "t" + (state.counter + 1),
        side: "native",
        sourceText: action.source,
        targetText: action.target,
        sourceLang: action.pair.native,
        targetLang: action.pair.foreign,
        status: action.status ?? "final",
        spoken: false,
        ts: Date.now(),
      });
      return { ...state, turns, counter: state.counter + 1 };
    }
    case "NATIVE_UPDATE": {
      const turns = state.turns.slice();
      const i = turns.findIndex((t) => t.id === action.id);
      if (i >= 0) turns[i] = { ...turns[i], ...action.patch };
      return { ...state, turns };
    }
    case "MARK_SPOKEN":
      return {
        ...state,
        turns: state.turns.map((t) =>
          t.id === action.id ? { ...t, spoken: true } : t
        ),
      };
    case "CLEAR":
      return { turns: [], openForeign: null, counter: 0 };
    default:
      return state;
  }
}
